export const API_BASE = import.meta.env.VITE_API_URL || 'https://api.specuno.com/api/v1';

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getAuthHeaders(extraHeaders: HeadersInit = {}): HeadersInit {
  const authData = localStorage.getItem('ba-auth');
  const token = authData ? JSON.parse(authData).state?.token : null;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  };
  
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: getAuthHeaders(options.headers),
  });
  
  const data = await response.json().catch(() => null);
  
  if (!response.ok) {
    throw new ApiError(
      response.status,
      data?.message || response.statusText,
      data
    );
  }
  
  return data as T;
}

export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint, { method: 'GET' }),
  
  post: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),
  
  patch: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),
  
  put: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),
  
  delete: <T>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),
  
  // Upload file (multipart form data)
  upload: async <T>(endpoint: string, formData: FormData): Promise<T> => {
    const url = `${API_BASE}${endpoint}`;
    
  const authData = localStorage.getItem('ba-auth');
  const token = authData ? JSON.parse(authData).state?.token : null;
  
  const headers: Record<string, string> = {};
  // Don't set Content-Type for FormData - browser will set it with boundary
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });
    
    const data = await response.json().catch(() => null);
    
    if (!response.ok) {
      throw new ApiError(
        response.status,
        data?.message || response.statusText,
        data
      );
    }
    
    return data as T;
  },
};

export { ApiError };

